import type { ProgrammeDetailPublic, ProgrammeStatus } from "@common/client"
import type { Column } from "./columns"
import { matchesFilters } from "./filters"
import type { ProgrammesDisplay, ProgrammesSearch, Tab } from "./search"
import { type SortValue, sortRows } from "./sort"

export function isArchived(programme: ProgrammeDetailPublic): boolean {
  return programme.archived_at !== null
}

export function archivedText(programme: ProgrammeDetailPublic): string {
  return `${programme.number} is archived. Restore it to make changes.`
}

export function enrolmentBlocker(
  programme: ProgrammeDetailPublic,
  canUpdate: boolean,
): string | undefined {
  if (!canUpdate) return "You can view aircraft but not change them."
  return isArchived(programme) ? archivedText(programme) : undefined
}

export type ProgrammeState = ProgrammeStatus | "archived"

export function stateOf(programme: ProgrammeDetailPublic): ProgrammeState {
  return isArchived(programme) ? "archived" : programme.status
}

export function stateLabel(state: ProgrammeState): string {
  switch (state) {
    case "draft":
      return "Draft"
    case "active":
      return "Active"
    case "archived":
      return "Archived"
  }
}

export function isActive(programme: ProgrammeDetailPublic): boolean {
  return stateOf(programme) === "active"
}

export function modelNames(programme: ProgrammeDetailPublic): string {
  return programme.model_variants
    .map((variant) => variant.marketing_name)
    .join(" / ")
}

function sortValue(
  programme: ProgrammeDetailPublic,
  column: Column,
): SortValue {
  const revision = programme.current_revision
  switch (column) {
    case "number":
      return programme.number
    case "title":
      return programme.title
    case "manufacturer":
      return programme.manufacturer ?? null
    case "models":
      return modelNames(programme) || null
    case "issue":
      return revision?.issue ?? null
    case "issue_date":
      return revision?.issue_date ?? null
    case "revision":
      return revision?.number ?? null
    case "revision_date":
      return revision?.effective_from ?? null
    case "tasks":
      return revision?.task_count ?? null
    case "aircraft":
      return programme.aircraft_count
    case "updated":
      return programme.updated_at
  }
}

function matchesText(programme: ProgrammeDetailPublic, text: string): boolean {
  const needle = text.toLowerCase()
  return [
    programme.number,
    programme.title,
    programme.manufacturer,
    modelNames(programme),
  ].some((value) => value?.toLowerCase().includes(needle))
}

function inTab(programme: ProgrammeDetailPublic, tab: Tab): boolean {
  switch (tab) {
    case "overview":
    case "all":
      return !isArchived(programme)
    case "active":
      return isActive(programme)
    case "archived":
      return isArchived(programme)
  }
}

export function shownProgrammes(
  programmes: ProgrammeDetailPublic[],
  search: ProgrammesSearch,
  display: ProgrammesDisplay,
): ProgrammeDetailPublic[] {
  return sortRows(
    programmes.filter(
      (programme) =>
        inTab(programme, search.tab ?? "all") &&
        (!search.search || matchesText(programme, search.search)) &&
        matchesFilters(programme, search),
    ),
    (programme) => sortValue(programme, display.sort),
    display.direction,
  )
}
